import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { FilterConfigRendererProps, FilterConfigSelect } from 'c-pagination';
import type { AutocompleteProps } from 'c-components';
import { Filterdropdown } from 'c-components';
import { useCommonTranslation } from 'c-translation';
import equal from 'fast-deep-equal';
import { sortByName } from 'c-main/Components/Campaign/CampaignReports/Views/ByField/ChartTypes/sorting';
import { Direction } from 'c-types';

type Props = FilterConfigRendererProps<FilterConfigSelect>;

const DropdownFilter: React.FC<Props> = ({ config, onChange, value, id, className }) => {
    const t = useCommonTranslation();

    /**
     * Some labels may be translation path strings. Attempt to translate them.
     */
    const [localVal, setLocalVal] = useState(value ?? []);
    const translatedOptions = useMemo(() => {
        const rawOptions =
            typeof config.options === 'function'
                ? (config.options as (currentValues?: any[]) => any[])(localVal ?? [])
                : config.options;

        const translated = rawOptions.map(opt => {
            const translatedLabel = t(opt.label);
            return {
                label: typeof translatedLabel === 'string' ? translatedLabel : opt.label,
                value: opt.value,
                disabled: opt.disabled ?? false,
            };
        });

        const enabledOptions = translated.filter(opt => !opt.disabled);
        const disabledOptions = translated.filter(opt => opt.disabled);

        // Sort each group alphabetically by label
        const sortedEnabledOptions = sortByName(enabledOptions, Direction.ASC, 'label', '', '');
        const sortedDisabledOptions = sortByName(disabledOptions, Direction.ASC, 'label', '', '');

        return [...sortedEnabledOptions, ...sortedDisabledOptions];
    }, [config, localVal, t]);

    const isMulti = useMemo(
        () => (config.multiple != null ? config.multiple : true),
        [config.multiple],
    );
    const onValueChange: AutocompleteProps['onChange'] = useCallback(
        (vals, reason) => {
            if (vals == null || reason === 'clear') {
                setLocalVal([]);
                onChange([]);
            } else {
                setLocalVal(Array.isArray(vals) ? vals : [vals]);
            }
        },
        [onChange],
    );

    useEffect(() => {
        setLocalVal(curr => (equal(curr, value) ? curr : value));
    }, [value]);

    const onClose = useCallback(() => {
        if (!equal(localVal, value)) {
            onChange(localVal);
        }
    }, [localVal, onChange, value]);

    const dropdownValue = useMemo(() => {
        if (isMulti) {
            return localVal;
        }

        return localVal?.[0];
    }, [isMulti, localVal]);

    const isSchedulesFilter = useMemo(() => String(id ?? '').includes('schedules'), [id]);
    const dropdownList = useMemo(
        () => (
            <Filterdropdown
                id={id}
                className={`entity-list-filters ${className}`}
                options={translatedOptions}
                multiple={isMulti}
                value={dropdownValue}
                placeholder={t(config.label)}
                onChange={onValueChange}
                onClose={onClose}
                componentsProps={
                    isSchedulesFilter
                        ? {
                              popper: {
                                  sx: {
                                      width: 'fit-content !important',
                                      maxWidth: '90vw',
                                  },
                              },
                          }
                        : undefined
                }
            />
        ),
        [
            id,
            className,
            translatedOptions,
            isMulti,
            dropdownValue,
            t,
            config.label,
            onValueChange,
            onClose,
            isSchedulesFilter,
        ],
    );
    return <>{dropdownList}</>;
};

export default DropdownFilter;
